/**
 * Capture flow screens (phone-first):
 *   /capture/:draftId                     overview — sections with progress
 *   /capture/:draftId/section/:code       one section
 *   /capture/:draftId/review              validation summary and submit
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { isNonCompliant, localize, progress, visibleControls, type Section, type Template, type ValidationIssue } from '@sss/form-engine';
import { Badge, Banner, Button, Card, Checkbox, Dialog, EmptyState, Icon, ProgressBar, ProgressRing, Spinner, SURVEY_STATUS_TONE } from '../../components';
import { cx } from '../../lib/cx';
import { useI18n } from '../../lib/i18n';
import { Link, useNavigate, useParams, useSearchParams } from '../../lib/router';
import type { DraftRecord } from '../../lib/offline/types';
import { ConnectedSyncIndicator } from '../../app/AppShell';
import { useServices } from '../../app/services';
import { useSyncStatus } from '../../app/hooks';
import { useToast } from '../../components/Toast';
import { isLockRejection, issuesFromDetails, rejectionKey } from '../../lib/sync/rejections';
import { isEditable, submissionIssues } from './draft-service';
import { SectionForm } from './SectionForm';
import { controlNumberOffset, useCapture } from './useCapture';

function sectionProgress(template: Template, section: Section, draft: DraftRecord, context: ReturnType<typeof useCapture>['context']) {
  return progress({ ...template, sections: [section] }, draft.answers, context);
}

export function SurveyIdLabel({ draft }: { draft: Pick<DraftRecord, 'humanId' | 'localRef'> }) {
  const { t } = useI18n();
  return draft.humanId ? (
    <span className="font-mono font-semibold">{draft.humanId}</span>
  ) : (
    <span className="text-muted">
      {t('ui.capture.notSynced')} · <span className="font-mono">{draft.localRef}</span>
    </span>
  );
}

function CaptureHeader({ title, subtitle, back, right }: { title: ReactNode; subtitle?: ReactNode; back: string; right?: ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <header className="pt-safe sticky top-0 z-30 -mx-4 -mt-4 mb-4 border-b border-border bg-surface/95 px-4 py-2.5 backdrop-blur-md lg:-mx-8 lg:-mt-6 lg:px-8">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => navigate(back)} aria-label={t('ui.common.back')} className="tap -ms-2 flex items-center justify-center rounded-lg hover:bg-surface-2">
          <Icon name="chevronLeft" size={22} className="rtl:rotate-180" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-lg font-bold leading-tight tracking-tight">{title}</h1>
          {subtitle && <div className="truncate text-sm">{subtitle}</div>}
        </div>
        {right}
      </div>
    </header>
  );
}

function BottomBar({ children }: { children: ReactNode }) {
  return (
    <div className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur lg:static lg:mt-6 lg:border-0 lg:bg-transparent lg:p-0">
      <div className="mx-auto flex max-w-3xl gap-2">{children}</div>
    </div>
  );
}

function useCaptureOrState(draftId: string | undefined) {
  const c = useCapture(draftId);
  const { t } = useI18n();
  let state: ReactNode = null;
  if (c.loading) state = <div className="flex justify-center py-20 text-brand"><Spinner size={32} /></div>;
  else if (!c.draft) state = <EmptyState icon="clipboard" title={t('ui.capture.notFound')} action={<Link to="/" className="font-semibold text-brand underline">{t('ui.nav.home')}</Link>} />;
  // A withdrawn survey only needs its explanation and the remove button, form or not.
  else if (c.draft.withdrawn && (c.templateMissing || !c.template)) state = <WithdrawnBanner draft={c.draft} />;
  else if (c.templateMissing || !c.template) state = <EmptyState icon="alert" title={t('ui.capture.templateMissing')} description={t('ui.capture.templateMissingBody')} />;
  return { c, state };
}

function LanguageToggle({ c }: { c: ReturnType<typeof useCapture> }) {
  const { t, lang } = useI18n();
  if (lang === 'en' || !c.template?.requiredLanguages.includes('en')) return null;
  return <Checkbox checked={c.showEnglish} onChange={c.setShowEnglish} label={t('ui.capture.showEnglish')} />;
}

/** Cancelled or given to another reporter on the server: nothing to sync, only remove it. */
function WithdrawnBanner({ draft }: { draft: DraftRecord }) {
  const { t } = useI18n();
  const { drafts } = useServices();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const w = draft.withdrawn!;
  const cancelled = w.reason === 'SURVEY_CANCELLED';
  return (
    <Banner
      tone="warning"
      title={cancelled ? t('ui.capture.cancelledTitle') : t('ui.capture.reassignedTitle')}
      action={
        <Button
          size="sm"
          variant="secondary"
          icon="trash"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            await drafts.discard(draft.id);
            navigate('/surveys', { replace: true });
          }}
        >
          {t('ui.capture.removeFromDevice')}
        </Button>
      }
    >
      {cancelled ? (w.note ? t('ui.capture.cancelledBodyReason', { reason: w.note }) : t('ui.capture.cancelledBody')) : t('ui.capture.reassignedBody')}
    </Banner>
  );
}

/**
 * Why the server refused this survey's last change, in words (C12), with the
 * way out: take the draft back from another device (LOCK_LOST / LOCK_HELD,
 * C7), retry, or — for a refused submit — the list of problems the server
 * found, each linking to its question.
 */
function SyncStateBanner({ draft, template }: { draft: DraftRecord; template?: Template | null }) {
  const { t } = useI18n();
  const { sync, api } = useServices();
  const status = useSyncStatus();
  const toast = useToast();
  if (draft.withdrawn) return <WithdrawnBanner draft={draft} />;
  if (draft.syncState !== 'rejected') return null;
  const lock = isLockRejection(draft.rejectedReason);
  // A refused submit is taken out of the queue (the survey is editable again): nothing to retry.
  const queued = (status.byEntity[draft.id] ?? 0) > 0;
  const issues = draft.rejectedReason === 'VALIDATION_FAILED' ? issuesFromDetails(draft.rejectedDetails) : [];
  return (
    <Banner
      tone="danger"
      title={t('ui.capture.rejectedTitle')}
      action={
        (lock || queued) && (
          <div className="flex flex-wrap gap-2">
            {lock && (
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  try {
                    await api.post(`/surveys/${draft.id}/lock`, { takeover: true });
                    await sync.retry(draft.id);
                  } catch {
                    toast.show(t('ui.capture.takeoverFailed'), 'danger');
                  }
                }}
              >
                {t('ui.capture.takeOver')}
              </Button>
            )}
            {queued && (
              <Button size="sm" variant="secondary" onClick={() => sync.retry(draft.id)}>
                {t('ui.common.retry')}
              </Button>
            )}
          </div>
        )
      }
    >
      <p>{t(rejectionKey(draft.rejectedReason))}</p>
      {issues.length > 0 && template && <IssueLinks draft={draft} template={template} issues={issues} />}
    </Banner>
  );
}

/** Problems found by the server (or the device), each a link to its question. */
function IssueLinks({ draft, template, issues }: { draft: DraftRecord; template: Template; issues: ValidationIssue[] }) {
  const { t, lang } = useI18n();
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {issues.map((i) => {
        const s = template.sections.find((x) => x.code === i.sectionCode);
        const it = s?.items.find((x) => (x.kind === 'field' && x.key === i.ref) || (x.kind === 'control' && x.code === i.ref));
        const label = !it ? i.ref : it.kind === 'control' ? `${it.code} · ${localize(it.question, lang, template.sourceLanguage)}` : it.kind === 'field' ? localize(it.label, lang, template.sourceLanguage) : i.ref;
        return (
          <li key={`${i.ref}-${i.code}`}>
            <Link to={`/capture/${draft.id}/section/${i.sectionCode}?issues=1&focus=${encodeURIComponent(i.itemId)}`} className="text-sm underline">
              {label} — {t(`ui.validation.${i.code}`)}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Files the server will never accept were taken out of the survey (C5): say which, until dismissed. */
function RefusedFilesBanner({ draft }: { draft: DraftRecord }) {
  const { t } = useI18n();
  const { drafts } = useServices();
  if (!draft.refusedFiles?.length) return null;
  return (
    <Banner tone="warning" title={t('ui.capture.filesRemovedTitle', { count: draft.refusedFiles.length })} onDismiss={() => void drafts.dismissRefusedFiles(draft.id)} dismissLabel={t('ui.common.dismiss')}>
      <ul className="flex flex-col gap-1">
        {draft.refusedFiles.map((f, i) => (
          <li key={`${f.fileName}-${i}`}>{t(`ui.capture.fileRefused.${f.code === 'UNSUPPORTED_TYPE' ? 'UNSUPPORTED_TYPE' : 'TOO_LARGE'}`, { name: f.fileName })}</li>
        ))}
      </ul>
      <p className="mt-1">{t('ui.capture.filesRemovedBody')}</p>
    </Banner>
  );
}

interface LoneWorkerSessionState {
  id: string;
  siteId: string;
  siteName: string;
  durationMinutes: number;
  expiresAt: string;
  status: string;
}

function LoneWorkerSafetyBar({ siteId, siteName, surveyId }: { siteId: string; siteName?: string; surveyId: string }) {
  const { api } = useServices();
  const toast = useToast();
  const [session, setSession] = useState<LoneWorkerSessionState | null>(null);
  const [busy, setBusy] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ session: LoneWorkerSessionState | null }>('/safety/lone-worker/active')
      .then((res) => {
        if (!cancelled && res.session) setSession(res.session);
      })
      .catch(() => {
        /* offline */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    if (!session || session.status !== 'ACTIVE') return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [session]);

  const startTimer = async (durationMinutes: number) => {
    setBusy(true);
    try {
      const res = await api.post<{ session: LoneWorkerSessionState }>('/safety/lone-worker/start', {
        siteId,
        surveyId,
        durationMinutes,
        lastZone: 'Perimeter & Site Capture',
      });
      setSession(res.session);
      toast.show(`Lone-Worker Safety Timer active (${durationMinutes} min welfare window)`, 'success');
    } catch {
      const expiresAt = new Date(Date.now() + durationMinutes * 60_000).toISOString();
      setSession({
        id: 'local-lw',
        siteId,
        siteName: siteName ?? 'Site',
        durationMinutes,
        expiresAt,
        status: 'ACTIVE',
      });
      toast.show(`Offline Lone-Worker Timer started (${durationMinutes} min)`, 'info');
    } finally {
      setBusy(false);
    }
  };

  const extendTimer = async () => {
    if (!session) return;
    setBusy(true);
    try {
      if (session.id !== 'local-lw') {
        const res = await api.post<{ expiresAt: string; durationMinutes: number }>(`/safety/lone-worker/${session.id}/extend`, {
          addMinutes: 30,
        });
        setSession({ ...session, expiresAt: res.expiresAt, durationMinutes: res.durationMinutes });
      } else {
        setSession({
          ...session,
          expiresAt: new Date(Math.max(Date.now(), Date.parse(session.expiresAt)) + 30 * 60_000).toISOString(),
          durationMinutes: session.durationMinutes + 30,
        });
      }
      toast.show('Lone-Worker check-in confirmed (+30 minutes)', 'success');
    } catch {
      toast.show('Extended local welfare timer (+30m)', 'info');
    } finally {
      setBusy(false);
    }
  };

  const checkoutTimer = async () => {
    if (!session) return;
    setBusy(true);
    try {
      if (session.id !== 'local-lw') {
        await api.post(`/safety/lone-worker/${session.id}/checkout`, {});
      }
      setSession(null);
      toast.show('Lone-Worker Safe Check-Out recorded', 'success');
    } catch {
      setSession(null);
    } finally {
      setBusy(false);
    }
  };

  const triggerSos = async () => {
    setBusy(true);
    try {
      if (session && session.id !== 'local-lw') {
        await api.post(`/safety/lone-worker/${session.id}/sos`, {
          note: 'Emergency SOS triggered by field assessor during site survey capture.',
        });
        setSession({ ...session, status: 'SOS_ESCALATED' });
      } else {
        await api.post('/safety/escalations', {
          siteId,
          surveyId,
          controlCode: 'LONE_WORKER_SOS',
          severity: 'CRITICAL',
          description: '[LONE-WORKER SOS / MAN-DOWN ALERT] Emergency SOS triggered from field capture device.',
        });
      }
      toast.show('🚨 Priority-1 LONE-WORKER SOS transmitted to Zero-Harm Command Center!', 'danger');
    } catch {
      toast.show('🚨 SOS alert queued for immediate transmission!', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const remSec = session ? Math.max(0, Math.round((Date.parse(session.expiresAt) - nowMs) / 1000)) : 0;
  const mins = Math.floor(remSec / 60);
  const secs = remSec % 60;

  return (
    <Card className={cx('p-3.5', session?.status === 'SOS_ESCALATED' ? 'border-danger bg-danger/5' : session ? 'border-brand/40 bg-brand/5' : '')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon name="shield" size={18} className={session?.status === 'SOS_ESCALATED' ? 'text-danger' : 'text-brand'} />
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-fg">Lone-Worker Welfare & Safety Timer</span>
              {session ? (
                <Badge tone={session.status === 'SOS_ESCALATED' ? 'danger' : remSec < 300 ? 'warning' : 'success'}>
                  {session.status === 'SOS_ESCALATED'
                    ? 'SOS ESCALATED'
                    : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')} REMAINING`}
                </Badge>
              ) : (
                <Badge tone="neutral">STANDBY</Badge>
              )}
            </div>
            <p className="text-xs text-muted">
              {session
                ? 'Automated welfare monitoring active — check in before countdown expires or trigger SOS in an emergency.'
                : 'Start a countdown check-in timer before entering isolated perimeters, vaults, or dark zones.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {!session ? (
            <>
              <Button size="sm" variant="secondary" loading={busy} onClick={() => startTimer(30)}>
                Start 30m
              </Button>
              <Button size="sm" variant="primary" loading={busy} onClick={() => startTimer(60)}>
                Start 60m Timer
              </Button>
            </>
          ) : (
            <>
              {session.status === 'ACTIVE' && (
                <>
                  <Button size="sm" variant="secondary" loading={busy} onClick={extendTimer}>
                    +30m Check-In
                  </Button>
                  <Button size="sm" variant="secondary" loading={busy} onClick={checkoutTimer}>
                    Safe Check-Out
                  </Button>
                </>
              )}
              <Button size="sm" variant="danger" icon="alert" loading={busy} onClick={triggerSos}>
                SOS Alert
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export function CaptureOverviewPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { c, state } = useCaptureOrState(draftId);
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const { drafts, api } = useServices();
  const toast = useToast();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [baseline, setBaseline] = useState<{
    surveyId: string;
    humanId: string;
    surveyDate: string | null;
    controls: Record<string, { value: 'YES' | 'NO' | 'NA' | null; note?: string; pin?: { x: number; y: number; zone?: string } }>;
  } | null>(null);
  const [deltaApplied, setDeltaApplied] = useState(false);

  const siteId = c.draft?.siteId;
  const status = c.draft?.status;
  useEffect(() => {
    if (!siteId || status !== 'DRAFT') return;
    let cancelled = false;
    api
      .get<{
        baseline: {
          surveyId: string;
          humanId: string;
          surveyDate: string | null;
          controls: Record<string, { value: 'YES' | 'NO' | 'NA' | null; note?: string; pin?: { x: number; y: number; zone?: string } }>;
        } | null;
      }>(`/surveys/site/${siteId}/baseline`)
      .then((res) => {
        if (!cancelled && res.baseline) setBaseline(res.baseline);
      })
      .catch(() => {
        /* offline or no baseline */
      });
    return () => {
      cancelled = true;
    };
  }, [api, siteId, status]);

  if (state) return <>{state}</>;
  const { draft, template } = c as Required<Pick<typeof c, 'draft' | 'template'>> & typeof c;
  const lang = c.displayLang;
  // Single pass per visible section: avoids 3x full-template scans on every keystroke in inline HDR.
  const sectionRows = c.visibleSections.map((s) => ({
    s,
    p: sectionProgress(template!, s, draft!, c.context),
    sectionFlags: s.items.filter((it) => (it.kind === 'field' ? draft!.flags[it.key] : it.kind === 'control' ? draft!.flags[it.code] : undefined)).length,
  }));
  const total = sectionRows.reduce((acc, { p }) => ({ answered: acc.answered + p.answered, total: acc.total + p.total }), { answered: 0, total: 0 });
  // HDR is rendered inline on this page, so "Continue" advances to the first incomplete control section.
  const firstIncomplete = sectionRows.find(({ s, p }) => s.code !== 'HDR' && p.answered < p.total)?.s;
  const flagged = Object.keys(draft!.flags).length;
  const editable = isEditable(draft!);

  const applyDeltaBaseline = () => {
    if (!baseline?.controls) return;
    let count = 0;
    for (const [code, prev] of Object.entries(baseline.controls)) {
      const current = draft!.answers.controls[code];
      if ((!current || current.value === null) && prev?.value) {
        c.setControl(code, {
          value: prev.value,
          note: prev.note ?? '',
          pin: prev.pin,
          baselineVerified: true,
        });
        count++;
      }
    }
    setDeltaApplied(true);
    toast.show(t('ui.excellence.delta.appliedToast', { count, humanId: baseline.humanId }), 'success');
  };

  const baselineCount = baseline ? Object.keys(baseline.controls).length : 0;

  return (
    <div className="mx-auto max-w-3xl pb-28 lg:pb-0">
      <CaptureHeader title={c.site?.name ?? t('ui.capture.survey')} subtitle={<SurveyIdLabel draft={draft!} />} back="/" right={<ConnectedSyncIndicator compact />} />
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={SURVEY_STATUS_TONE[draft!.status] ?? 'neutral'}>{t(`surveyStatus.${draft!.status}`)}</Badge>
          {c.site?.customerName && <span className="text-muted">{c.site.customerName}</span>}
        </div>
        {editable && draft!.siteId && (
          <LoneWorkerSafetyBar siteId={draft!.siteId} siteName={c.site?.name} surveyId={draft!.id} />
        )}
        <SyncStateBanner draft={draft!} template={template} />
        <RefusedFilesBanner draft={draft!} />
        {editable && draft!.status === 'DRAFT' && baseline && baselineCount > 0 && !deltaApplied && (
          <Banner
            tone="info"
            title={t('ui.excellence.delta.bannerTitle', { humanId: baseline.humanId })}
            action={
              <Button size="sm" variant="primary" icon="refresh" onClick={applyDeltaBaseline}>
                {t('ui.excellence.delta.applyButton', { count: baselineCount })}
              </Button>
            }
          >
            {t('ui.excellence.delta.bannerBody', {
              id: baseline.humanId,
              humanId: baseline.humanId,
              date: baseline.surveyDate ? fmt.date(baseline.surveyDate) : '—',
            })}
          </Banner>
        )}
        {draft!.status === 'RETURNED' && flagged > 0 && <Banner tone="warning" title={t('ui.capture.returnedTitle')}>{t('ui.capture.returnedBody', { count: flagged })}</Banner>}
        {c.carried.length > 0 && <Banner tone="danger">{t('ui.capture.carriedSummary', { count: c.carried.length })}</Banner>}
        {!editable && draft!.status !== 'RETURNED' && <Banner tone="info">{t('ui.capture.readOnly')}</Banner>}
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-semibold">{t('ui.capture.progress')}</span>
            <span className="tabular-nums text-muted">{t('ui.capture.answeredOf', { answered: total.answered, total: total.total })}</span>
          </div>
          <ProgressBar value={total.answered} max={total.total} label={t('ui.capture.progress')} tone={total.answered === total.total ? 'success' : 'brand'} />
        </Card>
        <LanguageToggle c={c} />
        <ul className="flex flex-col gap-2">
          {sectionRows.map(({ s, p, sectionFlags }) => {
            if (s.code === 'HDR') {
              return (
                <li key={s.code}>
                  <Card className="overflow-hidden p-0">
                    <button
                      type="button"
                      onClick={() => setHeaderExpanded((v) => !v)}
                      aria-expanded={headerExpanded}
                      className="flex min-h-18 w-full items-center gap-3 p-3 text-start transition-colors hover:bg-surface-2/60"
                    >
                      <ProgressRing value={p.answered} max={p.total} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{localize(s.title, lang, template!.sourceLanguage)}</p>
                        <p className="text-sm text-muted">{t('ui.capture.answeredOf', { answered: p.answered, total: p.total })}</p>
                      </div>
                      {sectionFlags > 0 && <Badge tone="warning" icon="flag">{sectionFlags}</Badge>}
                      <Icon name="chevronRight" className={cx('text-muted transition-transform duration-150', headerExpanded ? 'rotate-90' : 'rtl:rotate-180')} />
                    </button>
                    {headerExpanded && (
                      <div className="border-t border-border p-4">
                        <SectionForm
                          template={template!}
                          section={s}
                          draft={draft!}
                          context={c.context}
                          lang={lang}
                          actions={c.actions}
                          lookup={c.lookup}
                          setField={c.setField}
                          setControl={c.setControl}
                          carried={c.carried}
                          numberFrom={0}
                          editable={c.editableRefs}
                        />
                      </div>
                    )}
                  </Card>
                </li>
              );
            }
            return (
              <li key={s.code}>
                <Card className="flex min-h-18 items-center gap-3 p-3">
                  <Link to={`/capture/${draft!.id}/section/${s.code}`} className="flex min-w-0 flex-1 items-center gap-3">
                    <ProgressRing value={p.answered} max={p.total} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="truncate font-semibold">{localize(s.title, lang, template!.sourceLanguage)}</p>
                      </div>
                      <p className="text-sm text-muted">{t('ui.capture.answeredOf', { answered: p.answered, total: p.total })}</p>
                    </div>
                  </Link>
                  {sectionFlags > 0 && <Badge tone="warning" icon="flag">{sectionFlags}</Badge>}
                  <Link to={`/capture/${draft!.id}/section/${s.code}`} className="p-1 text-muted hover:text-fg">
                    <Icon name="chevronRight" className="rtl:rotate-180" />
                  </Link>
                </Card>
              </li>
            );
          })}
        </ul>
        {editable && draft!.status === 'DRAFT' && (
          <Button variant="ghost" icon="trash" className="self-start text-danger" onClick={() => setConfirmDiscard(true)}>
            {t('ui.capture.discard')}
          </Button>
        )}
      </div>
      {editable && (
        <BottomBar>
          {firstIncomplete && (
            <Button variant="secondary" size="lg" block onClick={() => navigate(`/capture/${draft!.id}/section/${firstIncomplete.code}`)}>
              {t('ui.capture.continue')}
            </Button>
          )}
          <Button size="lg" block iconEnd="chevronRight" onClick={() => navigate(`/capture/${draft!.id}/review`)}>
            {t('ui.capture.reviewSubmit')}
          </Button>
        </BottomBar>
      )}
      <Dialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title={t('ui.capture.discardTitle')}
        tone="danger"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>
              {t('ui.common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await drafts.discard(draft!.id);
                navigate('/', { replace: true });
              }}
            >
              {t('ui.capture.discard')}
            </Button>
          </>
        }
      >
        {t('ui.capture.discardBody')}
      </Dialog>
    </div>
  );
}

export function CaptureSectionPage() {
  const { draftId, code } = useParams<{ draftId: string; code: string }>();
  const [search] = useSearchParams();
  const { c, state } = useCaptureOrState(draftId);
  const { t } = useI18n();
  const navigate = useNavigate();
  const focus = search.get('focus');
  const showIssues = search.get('issues') === '1';

  useEffect(() => {
    if (!focus || state) return;
    const el = document.getElementById(`item-${focus}`);
    el?.scrollIntoView?.({ block: 'center' });
  }, [focus, state]);

  const issues: ValidationIssue[] = useMemo(
    () => (showIssues && c.template && c.draft ? submissionIssues(c.template, c.draft.answers, c.context) : []),
    [showIssues, c.template, c.draft, c.context],
  );

  if (state) return <>{state}</>;
  const { draft, template } = c;
  const idx = c.visibleSections.findIndex((s) => s.code === code);
  const section = c.visibleSections[idx] ?? template!.sections.find((s) => s.code === code);
  if (!section) return <EmptyState title={t('ui.common.notFound')} />;
  const prev = idx > 0 ? c.visibleSections[idx - 1] : undefined;
  const next = idx >= 0 ? c.visibleSections[idx + 1] : undefined;
  const p = sectionProgress(template!, section, draft!, c.context);
  const lang = c.displayLang;
  // HDR is rendered inline on the overview screen, so Previous from the first control section returns there.
  const go = (s: Section | undefined) =>
    navigate(
      !s
        ? `/capture/${draft!.id}/review`
        : s.code === 'HDR' && !showIssues
          ? `/capture/${draft!.id}`
          : `/capture/${draft!.id}/section/${s.code}${showIssues ? '?issues=1' : ''}`,
    );

  return (
    <div className="mx-auto max-w-3xl pb-28 lg:pb-0">
      <CaptureHeader
        title={localize(section.title, lang, template!.sourceLanguage)}
        subtitle={<span className="text-muted">{t('ui.capture.answeredOf', { answered: p.answered, total: p.total })}</span>}
        back={`/capture/${draft!.id}`}
        right={<ConnectedSyncIndicator compact />}
      />
      <div className="mb-4">
        <ProgressBar value={p.answered} max={p.total} tone={p.answered === p.total ? 'success' : 'brand'} label={t('ui.capture.progress')} />
      </div>
      <SectionForm
        template={template!}
        section={section}
        draft={draft!}
        context={c.context}
        lang={lang}
        actions={c.actions}
        lookup={c.lookup}
        setField={c.setField}
        setControl={c.setControl}
        issues={issues}
        carried={c.carried}
        numberFrom={controlNumberOffset(template!, c.visibleSections, section.code, draft!.answers, c.context)}
        editable={c.editableRefs}
      />
      <BottomBar>
        <Button variant="secondary" size="lg" icon="chevronLeft" disabled={!prev} onClick={() => go(prev)} className="rtl:[&_svg]:rotate-180">
          {t('ui.common.previous')}
        </Button>
        <Button size="lg" block iconEnd={next ? 'chevronRight' : 'check'} onClick={() => go(next)} className="rtl:[&_svg]:rotate-180">
          {next ? t('ui.common.next') : t('ui.capture.reviewSubmit')}
        </Button>
      </BottomBar>
    </div>
  );
}

export function SubmitReviewPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { c, state } = useCaptureOrState(draftId);
  const { t } = useI18n();
  const navigate = useNavigate();
  const { drafts } = useServices();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // What the submit found in the stored survey, when the screen's copy had looked complete.
  const [refused, setRefused] = useState<ValidationIssue[]>([]);
  const checked = useMemo(() => (c.template && c.draft ? submissionIssues(c.template, c.draft.answers, c.context) : []), [c.template, c.draft, c.context]);
  const issues = checked.length > 0 ? checked : refused;

  if (state) return <>{state}</>;
  const { draft, template } = c;
  const lang = c.displayLang;
  const editable = isEditable(draft!);
  const bySection = c.visibleSections
    .map((s) => ({ s, items: issues.filter((i) => i.sectionCode === s.code) }))
    .filter((g) => g.items.length > 0);
  const answers = draft!.answers;
  // Only count visible controls so answers/photos from unticked sections are excluded.
  const visControls = visibleControls(template!, answers, c.context);
  const nonCompliant = visControls.filter(({ control }) => isNonCompliant(control, answers.controls[control.code]?.value ?? null)).length;
  const photoCount = visControls.reduce((n, { control }) => n + (answers.controls[control.code]?.photos?.length ?? 0), 0);

  const itemLabel = (sectionCode: string, ref: string) => {
    const s = template!.sections.find((x) => x.code === sectionCode);
    const it = s?.items.find((i) => (i.kind === 'field' && i.key === ref) || (i.kind === 'control' && i.code === ref));
    if (!it) return ref;
    return it.kind === 'control' ? `${it.code} · ${localize(it.question, lang, template!.sourceLanguage)}` : it.kind === 'field' ? localize(it.label, lang, template!.sourceLanguage) : ref;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const remaining = await drafts.submit(draft!.id, template!, c.context);
      if (remaining.length === 0) {
        toast.show(t('ui.capture.submitted'), 'success');
        navigate('/', { replace: true });
        return;
      }
      // The stored survey is not what the screen showed: list its problems and show what is stored.
      setRefused(remaining);
      c.reload();
    } catch {
      toast.show(t('ui.capture.saveFailed'), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl pb-28 lg:pb-0">
      <CaptureHeader title={t('ui.capture.reviewSubmit')} subtitle={<SurveyIdLabel draft={draft!} />} back={`/capture/${draft!.id}`} />
      {!editable ? (
        <Banner tone="info">{t('ui.capture.readOnly')}</Banner>
      ) : bySection.length > 0 ? (
        <div className="flex flex-col gap-4">
          <Banner tone="warning" title={t('ui.capture.issuesTitle', { count: issues.length })}>
            {t('ui.capture.issuesBody')}
          </Banner>
          {bySection.map(({ s, items }) => (
            <Card key={s.code} className="p-4">
              <h2 className="mb-2 font-bold">{localize(s.title, lang, template!.sourceLanguage)}</h2>
              <ul className="flex flex-col divide-y divide-border">
                {items.map((i) => (
                  <li key={`${i.ref}-${i.code}`}>
                    <Link
                      to={`/capture/${draft!.id}/section/${s.code}?issues=1&focus=${encodeURIComponent(i.itemId)}`}
                      className="flex min-h-12 items-center gap-3 py-2"
                    >
                      <Icon name="alertCircle" className="shrink-0 text-danger" size={18} />
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 text-sm">{itemLabel(s.code, i.ref)}</span>
                        <span className="text-sm font-semibold text-danger">{t(`ui.validation.${i.code}`)}</span>
                      </span>
                      <Icon name="chevronRight" className="text-muted rtl:rotate-180" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Banner tone="success" title={t('ui.capture.readyTitle')}>
            {t('ui.capture.readyBody')}
          </Banner>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('ui.capture.statAnswered')} value={progress(template!, answers, c.context).answered} />
            <Stat label={t('ui.capture.statNotInPlace')} value={nonCompliant} tone={nonCompliant > 0 ? 'text-danger' : undefined} />
            <Stat label={t('ui.capture.statPhotos')} value={photoCount} />
          </div>
        </div>
      )}
      {editable && (
        <BottomBar>
          <Button size="lg" block icon="send" disabled={issues.length > 0} loading={busy} onClick={submit}>
            {draft!.status === 'RETURNED' ? t('ui.capture.resubmit') : t('ui.capture.submit')}
          </Button>
        </BottomBar>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <Card className="p-3 text-center">
      <p className={cx('text-2xl font-bold tabular-nums', tone)}>{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </Card>
  );
}
